// Modern modal implementation using react-modal
import React from 'react';
import ReactDOM from 'react-dom/client';
import ReactModal from 'react-modal';
import Button from '../components/Button';
import '../assets/css/modal.css';

// Set app element for accessibility
if (typeof window !== 'undefined') {
    ReactModal.setAppElement('#root');
}

// Track active modals
let activeModals = [];

// Store roots for cleanup
let modalRoots = {};

export const safeOpenModal = ({ config, component, componentProps, closeComponent, closeOnClickOutside }) => {
    console.log('safeOpenModal called with config:', config);
    console.log('Component to render:', component);

    try {
        if (!component) {
            console.error('No component provided to safeOpenModal');
            return null;
        }

        const ModalComponent = component;
        const CloseButton = closeComponent;

        // Create a container for the modal
        const modalRoot = document.createElement('div');
        const modalId = 'modal-root-' + Date.now();
        modalRoot.id = modalId;
        document.body.appendChild(modalRoot);
        console.log('Modal root created:', modalId);

        // Check if root element exists for ReactModal
        const rootElement = document.getElementById('root');
        if (!rootElement) {
            console.error('No #root element found for ReactModal.setAppElement');
        } else {
            console.log('#root element found for ReactModal');
            // Ensure app element is set
            ReactModal.setAppElement('#root');
        }

        // Function to safely close this specific modal
        const closeModal = () => {
            console.log('Closing modal', modalId);
            if (modalRoots[modalId]) {
                modalRoots[modalId].unmount();
                delete modalRoots[modalId];

                if (modalRoot && modalRoot.parentNode) {
                    modalRoot.parentNode.removeChild(modalRoot);
                }

                // Remove from active modals
                activeModals = activeModals.filter(m => m !== modalId);

                // Only remove body class if no more modals are open
                if (activeModals.length === 0) {
                    document.body.classList.remove('modalOpen');
                }
            }
        };

        // Add to active modals
        activeModals.push(modalId);

        // Add modal open class to body
        document.body.classList.add('modalOpen');

        // Create modal content with the configured component
        const modalContent = (
            <ReactModal
                isOpen={true}
                onRequestClose={closeOnClickOutside ? closeModal : undefined}
                className={`${config?.className || 'modal-content'} modal-content`}
                overlayClassName="modal-overlay"
                shouldCloseOnOverlayClick={closeOnClickOutside}
                contentLabel="Modal"
            >
                {CloseButton && <CloseButton onClick={closeModal} />}
                <ModalComponent {...componentProps} closeModal={closeModal} />
            </ReactModal>
        );

        // Create root and render
        const root = ReactDOM.createRoot(modalRoot);
        modalRoots[modalId] = root;
        root.render(modalContent);

        console.log('Modal opened successfully');

        // Return object with close method for compatibility
        return { close: closeModal };
    } catch (error) {
        console.error('Error opening modal:', error);
        return null;
    }
};

export const safeCloseModal = () => {
    console.log('safeCloseModal called');
    try {
        // Close all active modals
        Object.keys(modalRoots).forEach(id => {
            if (modalRoots[id]) {
                modalRoots[id].unmount();
                delete modalRoots[id];

                const modalRoot = document.getElementById(id);
                if (modalRoot && modalRoot.parentNode) {
                    modalRoot.parentNode.removeChild(modalRoot);
                }
            }
        });

        // Clear active modals
        activeModals = [];

        // Remove modal open class
        document.body.classList.remove('modalOpen');

        console.log('All modals closed successfully');
    } catch (error) {
        console.error('Error closing modal:', error);
    }
};

// Default close button for modal
const CloseModalButton = ({ onClick }) => (
    <Button
        className="modalCloseBtn"
        variant="fab"
        onClick={onClick || safeCloseModal}
        icon={<i className="flaticon-plus-symbol" style={{ fontSize: '2rem', color: '#fff', }} />}
    />
);

// Calendar iframe component for the modal with loading spinner
const CalendarIframeContent = ({ url }) => {
    const [isLoading, setIsLoading] = React.useState(true);

    return (
        <div className="calendar-iframe-container">
            {isLoading && (
                <div className="calendar-loading-spinner">
                    <div className="spinner"></div>
                </div>
            )}
            <iframe
                src={url}
                title="Schedule a Meeting"
                width="100%"
                height="100%"
                frameBorder="0"
                style={{ border: 'none', opacity: isLoading ? 0 : 1 }}
                onLoad={() => setIsLoading(false)}
            />
        </div>
    );
};

// Google Calendar URL
const GOOGLE_CALENDAR_URL = 'https://calendar.google.com/calendar/u/0/appointments/schedules/AcZssZ3A6lvh50QcS19sBgq6tQHN6MlpZAezBcfQJk1HkN0odU3HV2XJvBDE5DRzAsxSJRMnGFTh4UhQ';

// Open calendar modal function
export const openCalendarModal = () => {
    console.log('openCalendarModal called');
    try {
        safeOpenModal({
            config: {
                className: 'calendar-modal',
                disableDragging: true,
                width: '95%',
                height: '95%',
                margin: 0,
                padding: 0
            },
            component: CalendarIframeContent,
            componentProps: { url: GOOGLE_CALENDAR_URL },
            closeComponent: CloseModalButton,
            closeOnClickOutside: true
        });

        return { close: safeCloseModal };
    } catch (error) {
        console.error('Error in openCalendarModal:', error);
        return null;
    }
}; 